
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ShoppingCart, Eye, Trash2 } from 'lucide-react';
import { useComparison } from '@/contexts/ComparisonContext';
import { useOptimizedCart } from '@/contexts/OptimizedCartContext';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/header/ModernHeader';
import Footer from '@/components/Footer';
import WishlistButton from '@/components/WishlistButton';
import { useToast } from '@/hooks/use-toast';

const Comparison = () => {
  const { items, removeFromComparison, clearComparison } = useComparison();
  const { addToCart } = useOptimizedCart();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleAddToCart = (product: any) => {
    addToCart(product);
  };

  const handleViewProduct = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-6xl mb-6">🔍</div>
            <h1 className="text-3xl font-bold text-foreground mb-4">No Products to Compare</h1>
            <p className="text-muted-foreground mb-8">
              Add products to your comparison list to see them here.
            </p>
            <Button asChild size="lg">
              <div onClick={() => navigate('/products')}>Browse Products</div>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Generate comparison features from all products
  const allFeatures = new Set<string>();
  items.forEach(item => {
    allFeatures.add('Price');
    allFeatures.add('Category');
    allFeatures.add('Stock Status');
    allFeatures.add('Description');
    if (item.compare_at_price) allFeatures.add('Original Price');
  });

  const featuresList = Array.from(allFeatures);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/products')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Product Comparison</h1>
              <p className="text-muted-foreground">
                Compare {items.length} {items.length === 1 ? 'product' : 'products'}
              </p>
            </div>
            <Button variant="outline" onClick={clearComparison}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          </div>
        </div>

        {/* Mobile View */}
        <div className="lg:hidden space-y-4">
          {items.map((item) => (
            <Card key={item.id}>
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-muted rounded-md overflow-hidden flex-shrink-0">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        📦
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{item.name}</CardTitle>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="text-xl font-bold text-green-600">
                        ${item.price.toFixed(2)}
                      </span>
                      {item.compare_at_price && (
                        <span className="text-sm text-muted-foreground line-through">
                          ${item.compare_at_price.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <span className="font-medium">Category: </span>
                    <span>{item.categories?.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium">Stock: </span>
                    <Badge variant={(item.stock_quantity || 0) > 0 ? 'default' : 'destructive'}>
                      {(item.stock_quantity || 0) > 0 ? 'In Stock' : 'Out of Stock'}
                    </Badge>
                  </div>
                  {item.description && (
                    <div>
                      <span className="font-medium">Description: </span>
                      <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                    </div>
                  )}
                  <div className="flex gap-2 pt-4">
                    <Button size="sm" onClick={() => handleViewProduct(item.id)}>
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => handleAddToCart(item)}
                      disabled={(item.stock_quantity || 0) === 0}
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Add to Cart
                    </Button>
                    <WishlistButton productId={item.id} size="sm" />
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => removeFromComparison(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden lg:block">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4 w-48">Feature</th>
                      {items.map((item) => (
                        <th key={item.id} className="text-center p-4 min-w-64">
                          <div className="space-y-3">
                            <div className="w-32 h-32 bg-muted rounded-md overflow-hidden mx-auto">
                              {item.image_url ? (
                                <img
                                  src={item.image_url}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                  📦
                                </div>
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold text-sm">{item.name}</h3>
                              <div className="flex items-center justify-center space-x-2 mt-2">
                                <span className="text-lg font-bold text-green-600">
                                  ${item.price.toFixed(2)}
                                </span>
                                {item.compare_at_price && (
                                  <span className="text-xs text-muted-foreground line-through">
                                    ${item.compare_at_price.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1 justify-center">
                              <Button size="sm" onClick={() => handleViewProduct(item.id)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                onClick={() => handleAddToCart(item)}
                                disabled={(item.stock_quantity || 0) === 0}
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </Button>
                              <WishlistButton productId={item.id} size="sm" />
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => removeFromComparison(item.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {featuresList.map((feature) => (
                      <tr key={feature} className="border-b">
                        <td className="p-4 font-medium">{feature}</td>
                        {items.map((item) => (
                          <td key={item.id} className="p-4 text-center">
                            {feature === 'Price' && `$${item.price.toFixed(2)}`}
                            {feature === 'Original Price' && (
                              item.compare_at_price ? `$${item.compare_at_price.toFixed(2)}` : 'N/A'
                            )}
                            {feature === 'Category' && (item.categories?.name || 'N/A')}
                            {feature === 'Stock Status' && (
                              <Badge variant={(item.stock_quantity || 0) > 0 ? 'default' : 'destructive'}>
                                {(item.stock_quantity || 0) > 0 ? 'In Stock' : 'Out of Stock'}
                              </Badge>
                            )}
                            {feature === 'Description' && (
                              <div className="text-sm text-muted-foreground max-w-48 mx-auto">
                                {item.description || 'N/A'}
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Comparison;
